import { motion } from "motion/react";
import { Heart, Star, Target } from "lucide-react";

const visionPoints = [
  {
    icon: Heart,
    title: "Passion for Education",
    description:
      "A deep belief that every child deserves access to quality education that nurtures both mind and character.",
  },
  {
    icon: Star,
    title: "Excellence as Standard",
    description:
      "Setting the bar high and providing the resources and support needed for every student to reach their potential.",
  },
  {
    icon: Target,
    title: "Holistic Development",
    description:
      "Educating the whole child—academically, socially, emotionally, and physically—to prepare them for life.",
  },
];

export function FoundersDream() {
  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6 border border-white/20"
          >
            Our Beginning
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl font-bold text-white mb-6"
          >
            The Founder's Dream
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-white/80 max-w-2xl mx-auto"
          >
            A vision born from passion, dedication, and an unwavering commitment
            to transforming lives through education.
          </motion.p>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/founders.jpeg"
                  alt="School Founder"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-red-600 rounded-2xl flex items-center justify-center shadow-xl">
                <div className="text-center text-white">
                  <div className="text-2xl font-bold">Est.</div>
                  <div className="text-xl">2005</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-4">
                The Vision
              </span>
              

              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                The idea of starting a private school was first mooted in the mid-1970s by Dorcas Njeri Wafula, the wife of the founder chairman, Hillary Pius Wafula. At the time, as a Head Teacher in one of the local primary schools in Kitale, she saw a gap in the provision of quality primary school education. She also realized children attending schools located within town performed poorly in national examinations due to noise, continuous disruptions and too much exposure to the wrong environment. Upon her retirement from Teacher Service Commission in the early 1980s, Dorcas began the process of putting her thought together of what her “dream school” would look like. Many years went by as she continued to put the pieces of the puzzle together cumulating to the eventually actualized of her dream in 2005 with the establishment of Hilltop Preparatory School on their farm, Bridge Dairy Farm, on top a hill that allowed for the best view of the surrounding serene environment that was ideal for children to grow and learn in.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                The founders’ dream was to provide all children with a safe and secure environment that encouraged the child to realize their full potential as they grew in a warm, positive and homely environment. This, in their view, was key for the all-round development of the child in a fast-changing global environment. Both founders of Hilltop came from very humble backgrounds to excel in their lives and it is with this background in mind, that Hilltop has continued to establish itself as the environment where all children would make their educational and life journey a success.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision Points */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Core Beliefs
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900">
              Pillars of the Dream
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {visionPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="group"
              >
                <div className="bg-white rounded-3xl p-8 h-full shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-slate-100 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <point.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">
                    {point.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {point.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Legacy */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-8">
              The Dream Lives On
            </h2>
            <p className="text-xl text-white/80 leading-relaxed mb-8">
              The founders have since passed on but their dreams have continued to live on. From its very humble beginnings in 2005 with an initial student population of 12, Hilltop Preparatory School now boasts of over 500 students population. The new leadership of the school has exciting new ideas for growth including expansion into a high school on the land adjacent to the current school, enhancement of school ground for extra-curriculum activities, building a competition class swimming pool, indoor sports arena, staff housing among many other ideas.
            </p>
            <p className="text-lg text-white/70 leading-relaxed">
              Over the years, Hilltop has become an institution that continuously excelled in the KCPE national examinations and has continued to produce some of the country’s top students, who have made significant strides in life after Hilltop.As we look into the future, the Board of directors and Shareholders are committed to growing and developing Hilltop as we live our founders’ dream and maintain the legacy.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
